/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { EntityTenantSettingsSchema } from '../../Entities/TenantSettingsSchema/TenantSettingsSchema.entity';
import { getTenantSettingsService } from '../../Utilities/GetTenantSettingsService/GetTenantSettingsService';

/**
 * The result expected of the useSchema hook.
 */
export type TenantSettingsSchemaResult = [EntityTenantSettingsSchema | null, JsonObject | null];

/**
 * React hook to get a tenant settings schema by schema ID.
 */
export function useSchema(schemaId: string): TenantSettingsSchemaResult {
  const tenantSettingsService = getTenantSettingsService();

  const schema$ = useMemo((): Observable<EntityTenantSettingsSchema | null> => {
    return tenantSettingsService.getSchema({ schemaId });
  }, [schemaId, tenantSettingsService]);

  const [schema, error] = useObservable(schema$, [schemaId, tenantSettingsService]);

  return useMemo((): TenantSettingsSchemaResult => {
    return [schema as EntityTenantSettingsSchema, error as JsonObject];
  }, [error, schema]);
}
